import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../../firebase';
import usePortvilleRoomAuth from '../../../hooks/admin/usePortvilleRoomAuth';
import { PortvilleResidency } from '../../../types/portvilleRoom';
import { setAdminScrollTop } from '../../../utils/adminScroll';
import { useBackToPortvilleRoomsListOnPop } from '../../../hooks/useBackToPortvilleRoomsListOnPop';
import { markPortvilleRoomsRestoreOnNext } from '../../../utils/portvilleRoomsScroll';
import { downloadStorageFile } from '../../../utils/storageDownload';

type ExtensionDocumentType = 'identification' | 'employment' | 'other';
type ExtensionDocuments = Partial<Record<ExtensionDocumentType, string>>;

const getDocumentDisplayName = (docType: ExtensionDocumentType) => {
  switch (docType) {
    case 'identification':
      return '신분증 사본';
    case 'employment':
      return '재직증명서/사업자등록증';
    case 'other':
      return '기타 서류';
  }
};

const getOriginalFileNameFromStoragePath = (storagePath: string) => {
  const fileName = storagePath.split('/').pop() || storagePath;
  const extracted = fileName.split('_').slice(3).join('_');
  return extracted || fileName;
};

const ExtensionRequestPage: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loading: authLoading, hasAccess } = usePortvilleRoomAuth();
  const [loading, setLoading] = useState(true);
  const [residencyInfo, setResidencyInfo] = useState<PortvilleResidency | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [extensionDocuments, setExtensionDocuments] = useState<ExtensionDocuments>({});
  const [extensionDocumentsError, setExtensionDocumentsError] = useState<string | null>(null);
  const [extensionDocumentsDownloading, setExtensionDocumentsDownloading] = useState<ExtensionDocumentType | null>(null);
  const [extensionRound, setExtensionRound] = useState<1 | 2>(1);

  useBackToPortvilleRoomsListOnPop();

  const goToRoomsList = () => {
    markPortvilleRoomsRestoreOnNext();
    navigate('/admin/portville-rooms', { state: { restoreScroll: true } });
  };

  useEffect(() => {
    setAdminScrollTop(0);
  }, []);

  useEffect(() => {
    if (!authLoading && !hasAccess) {
      return;
    }
    
    if (roomId) {
      loadResidencyInfo();
    }
  }, [roomId, authLoading, hasAccess, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadResidencyInfo = async () => {
    if (!roomId) return;
    
    try {
      setLoading(true);
      setError(null);
      setExtensionDocumentsError(null);
      
      // portville_rooms에서 현재 입주 기록 ID 가져오기
      const roomRef = doc(db, 'portville_rooms', roomId);
      const roomDoc = await getDoc(roomRef);
      
      if (!roomDoc.exists()) {
        setError('호실 정보를 찾을 수 없습니다.');
        return;
      }
      
      const roomData = roomDoc.data();
      if (!roomData.currentResidencyId) {
        setError('현재 입주 기록이 없습니다.');
        return;
      }
      
      // portville_residencies에서 입주 기록 가져오기
      const residencyRef = doc(db, 'portville_residencies', roomData.currentResidencyId);
      const residencyDoc = await getDoc(residencyRef);
      
      if (!residencyDoc.exists()) {
        setError('입주 기록을 찾을 수 없습니다.');
        return;
      }
      
      const residencyData = residencyDoc.data();
      const requestedRound = searchParams.get('round') === '2' ? 2 : searchParams.get('round') === '1' ? 1 : null;
      const inferredRound = residencyData.secondExtensionStatus === 'applied' ? 2 : 1;
      const effectiveRound = requestedRound ?? inferredRound;

      if (effectiveRound === 2 && residencyData.hasExtended !== true) {
        setError('2차 연장 신청은 1차 연장 완료 후 가능합니다.');
        return;
      }

      setExtensionRound(effectiveRound);
      const documentsField =
        effectiveRound === 2 ? residencyData.secondExtensionDocuments : residencyData.extensionDocuments;
      setExtensionDocuments((documentsField || {}) as ExtensionDocuments);
      
      // residentInfo가 없거나 연락처/소속이 없는 경우 user_profiles에서 가져오기
      let residentInfo = residencyData.residentInfo;
      if (!residentInfo || !residentInfo.phoneNumber || !residentInfo.organization) {
        console.log('사용자 정보 부족, user_profiles에서 가져옴:', residencyData.residentUid);
        
        try {
          const userProfileRef = doc(db, 'user_profiles', residencyData.residentUid);
          const userProfileDoc = await getDoc(userProfileRef);
          
          if (userProfileDoc.exists()) {
            const userProfile = userProfileDoc.data();
            residentInfo = {
              name: residentInfo?.name || userProfile.name || '',
              phoneNumber: residentInfo?.phoneNumber || userProfile.phoneNumber || '',
              organization: residentInfo?.organization || userProfile.organization || ''
            };
            
            // 기존 residentInfo가 부족한 경우 업데이트
            if (!residencyData.residentInfo?.phoneNumber || !residencyData.residentInfo?.organization) {
              await updateDoc(residencyRef, {
                residentInfo: residentInfo,
                updatedAt: Timestamp.now()
              });
              console.log('입주 기록에 사용자 정보 업데이트 완료');
            }
          }
        } catch (userProfileError) {
          console.error('사용자 프로필 조회 실패:', userProfileError);
        }
      }
      
      const residency: PortvilleResidency = {
        id: residencyDoc.id,
        roomId: residencyData.roomId,
        roomNumber: residencyData.roomNumber,
        residentUid: residencyData.residentUid,
        residentInfo: residentInfo,
        status: residencyData.status,
        occupancyPeriod: residencyData.occupancyPeriod ? {
          startDate: residencyData.occupancyPeriod.startDate.toDate(),
          endDate: residencyData.occupancyPeriod.endDate.toDate()
        } : undefined,
        roommateInfo: residencyData.roommateInfo,
        vehicleInfo: residencyData.vehicleInfo,
        notes: residencyData.notes,
        signatureName: residencyData.signatureName,
        signatureDate: residencyData.signatureDate?.toDate(),
        signatureUrl: residencyData.signatureUrl,
        contractSignedAt: residencyData.contractSignedAt?.toDate(),
        moveInInspectionSignedAt: residencyData.moveInInspectionSignedAt?.toDate(),
        moveInInspectionSignatureUrl: residencyData.moveInInspectionSignatureUrl,
        landlordSignedContractUrl: residencyData.landlordSignedContractUrl,
        landlordSignedContractUploadedAt: residencyData.landlordSignedContractUploadedAt?.toDate(),
        contractContent: residencyData.contractContent,
        hasExtended: residencyData.hasExtended || false,
        extensionStatus: residencyData.extensionStatus || 'none',
        extensionAppliedAt: residencyData.extensionAppliedAt?.toDate(),
        extensionApprovedAt: residencyData.extensionApprovedAt?.toDate(),
        originalEndDate: residencyData.originalEndDate?.toDate(),
        extensionDocuments: residencyData.extensionDocuments,
        extensionDocumentsUpdatedAt: residencyData.extensionDocumentsUpdatedAt?.toDate?.(),
        hasSecondExtended: residencyData.hasSecondExtended || false,
        secondExtensionStatus: residencyData.secondExtensionStatus || 'none',
        secondExtensionAppliedAt: residencyData.secondExtensionAppliedAt?.toDate(),
        secondExtensionApprovedAt: residencyData.secondExtensionApprovedAt?.toDate(),
        secondExtensionRejectedAt: residencyData.secondExtensionRejectedAt?.toDate(),
        secondExtensionRejectionReason: residencyData.secondExtensionRejectionReason,
        secondOriginalEndDate: residencyData.secondOriginalEndDate?.toDate(),
        secondExtensionDocuments: residencyData.secondExtensionDocuments,
        secondExtensionDocumentsUpdatedAt: residencyData.secondExtensionDocumentsUpdatedAt?.toDate?.(),
        createdAt: residencyData.createdAt.toDate(),
        updatedAt: residencyData.updatedAt.toDate(),
        completedAt: residencyData.completedAt?.toDate()
      };
      
      const currentStatus = effectiveRound === 2 ? residency.secondExtensionStatus : residency.extensionStatus;
      if (currentStatus !== 'applied') {
        setError(`${effectiveRound}차 계약 연장 신청이 없습니다.`);
        return;
      }
      
      setResidencyInfo(residency);
      
    } catch (error) {
      console.error('입주 정보 로드 실패:', error);
      setError('입주 정보를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!residencyInfo) return;
    
    try {
      setProcessing(true);
      
      // 사용자 정보 업데이트 확인 및 처리
      const updateData: any = {
        updatedAt: Timestamp.now()
      };

      if (extensionRound === 2) {
        updateData.secondExtensionStatus = 'approved';
        updateData.secondExtensionApprovedAt = Timestamp.now();
        updateData.hasSecondExtended = true;
      } else {
        updateData.extensionStatus = 'approved';
        updateData.extensionApprovedAt = Timestamp.now();
        updateData.hasExtended = true;
      }
      
      // residentInfo가 부족한 경우 user_profiles에서 가져와서 업데이트
      if (!residencyInfo.residentInfo?.phoneNumber || !residencyInfo.residentInfo?.organization) {
        try {
          const userProfileRef = doc(db, 'user_profiles', residencyInfo.residentUid);
          const userProfileDoc = await getDoc(userProfileRef);
          
          if (userProfileDoc.exists()) {
            const userProfile = userProfileDoc.data();
            updateData.residentInfo = {
              name: residencyInfo.residentInfo?.name || userProfile.name || '',
              phoneNumber: residencyInfo.residentInfo?.phoneNumber || userProfile.phoneNumber || '',
              organization: residencyInfo.residentInfo?.organization || userProfile.organization || ''
            };
            console.log('승인 시 사용자 정보 업데이트:', updateData.residentInfo);
          }
        } catch (userProfileError) {
          console.error('사용자 프로필 조회 실패:', userProfileError);
        }
      }
      
      // 계약 연장 승인 처리
      const residencyRef = doc(db, 'portville_residencies', residencyInfo.id);
      await updateDoc(residencyRef, updateData);
      
      alert('계약 연장 신청이 승인되었습니다. 연장 계약 날인 단계로 이동합니다.');
      navigate(`/admin/portville-rooms/extension-contract/${roomId}?round=${extensionRound}`);
      
    } catch (error) {
      console.error('승인 처리 실패:', error);
      alert('승인 처리 중 오류가 발생했습니다.');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!residencyInfo) return;
    
    const reason = prompt('거부 사유를 입력해주세요:');
    if (!reason) return;
    
    try {
      setProcessing(true);
      
      // 계약 연장 거부 처리
      const residencyRef = doc(db, 'portville_residencies', residencyInfo.id);
      if (extensionRound === 2) {
        await updateDoc(residencyRef, {
          secondExtensionStatus: 'rejected',
          secondExtensionRejectedAt: Timestamp.now(),
          secondExtensionRejectionReason: reason,
          updatedAt: Timestamp.now()
        });
      } else {
        await updateDoc(residencyRef, {
          extensionStatus: 'rejected',
          extensionRejectedAt: Timestamp.now(),
          extensionRejectionReason: reason,
          updatedAt: Timestamp.now()
        });
      }
      
      alert('계약 연장 신청이 거부되었습니다.');
      goToRoomsList();
      
    } catch (error) {
      console.error('거부 처리 실패:', error);
      alert('거부 처리 중 오류가 발생했습니다.');
    } finally {
      setProcessing(false);
    }
  };

  if (authLoading) {
    return (
      <div className="w-full min-h-screen bg-[#f7f9fd]">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#155acb]"></div>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return null;
  }

  if (loading) {
    return (
      <div className="w-full min-h-screen bg-[#f7f9fd]">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#155acb]"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full min-h-screen bg-[#f7f9fd]">
        <div className="max-w-[1600px] mx-auto pt-8 px-6">
          <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
            <div className="p-6 bg-red-50 border-l-4 border-red-500 rounded-r-sm">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800 font-['Pretendard']">{error}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4">
            <button
              onClick={goToRoomsList}
              className="px-4 py-2 bg-[#155acb] text-white rounded-sm hover:bg-[#155acb]/90 font-['Pretendard']"
            >
              목록으로 돌아가기
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!residencyInfo) {
    return null;
  }

  return (
    <div className="w-full min-h-screen bg-[#f7f9fd]">
      <div className="max-w-[1600px] mx-auto pt-8 px-6">
        {/* 헤더 */}
        <div className="mt-8">
          <div className="flex items-center justify-between">
            <div className="text-slate-800 text-4xl font-extrabold font-['Pretendard'] leading-9">
              계약 연장 신청 확인
            </div>
            <button
              onClick={goToRoomsList}
              className="px-4 py-2 bg-gray-500 text-white rounded-sm hover:bg-gray-600 font-['Pretendard']"
            >
              목록으로 돌아가기
            </button>
          </div>
          <div className="w-full h-px mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>
        </div>

        {/* 신청 정보 카드 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-8">
            <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-6">
              계약 연장 신청 정보
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* 호실 정보 */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-[#1e2c49] font-['Pretendard']">호실 정보</h3>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm font-medium text-gray-500">호실번호:</span>
                    <span className="ml-2 text-sm text-[#1e2c49]">{residencyInfo.roomNumber}호</span>
                  </div>
                </div>
              </div>

              {/* 입주자 정보 */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-[#1e2c49] font-['Pretendard']">입주자 정보</h3>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm font-medium text-gray-500">성명:</span>
                    <span className="ml-2 text-sm text-[#1e2c49]">{residencyInfo.residentInfo.name}</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">연락처:</span>
                    <span className="ml-2 text-sm text-[#1e2c49]">{residencyInfo.residentInfo.phoneNumber}</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">소속:</span>
                    <span className="ml-2 text-sm text-[#1e2c49]">{residencyInfo.residentInfo.organization}</span>
                  </div>
                </div>
              </div>

              {/* 계약 기간 정보 */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-[#1e2c49] font-['Pretendard']">현재 계약 기간</h3>
                <div className="space-y-2">
                  {residencyInfo.occupancyPeriod && (
                    <>
                      <div>
                        <span className="text-sm font-medium text-gray-500">시작일:</span>
                        <span className="ml-2 text-sm text-[#1e2c49]">
                          {residencyInfo.occupancyPeriod.startDate.toLocaleDateString('ko-KR')}
                        </span>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-500">종료일:</span>
                        <span className="ml-2 text-sm text-[#1e2c49]">
                          {residencyInfo.occupancyPeriod.endDate.toLocaleDateString('ko-KR')}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* 신청 정보 */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-[#1e2c49] font-['Pretendard']">연장 신청 정보</h3>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm font-medium text-gray-500">신청일:</span>
                    <span className="ml-2 text-sm text-[#1e2c49]">
                      {residencyInfo.extensionAppliedAt?.toLocaleString('ko-KR')}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">상태:</span>
                    <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                      연장신청
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 연장 신청 첨부파일 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-1">
                  연장 신청 첨부파일
                </h2>
                <p className="text-sm text-gray-600 font-['Pretendard']">
                  사용자가 연장 신청 전에 업로드한 서류입니다.
                </p>
              </div>
              {extensionDocumentsDownloading && (
                <div className="text-sm text-gray-500 font-['Pretendard']">다운로드 중...</div>
              )}
            </div>

            {extensionDocumentsError && (
              <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-800 text-sm font-['Pretendard']">
                {extensionDocumentsError}
              </div>
            )}

            <div className="mt-4 space-y-2">
              {(['identification', 'employment', 'other'] as const).map((type) => {
                const storagePath = extensionDocuments[type];
                const isRequired = type === 'identification' || type === 'employment';
                const isDownloading = extensionDocumentsDownloading === type;

                return (
                  <div
                    key={type}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border border-gray-200 rounded p-3 bg-gray-50"
                  >
                    <div className="text-sm text-gray-800 font-['Pretendard']">
                      <span className="font-medium">{getDocumentDisplayName(type)}</span>
                      {isRequired && <span className="ml-2 text-xs text-red-600">(필수)</span>}
                      {storagePath ? (
                        <span className="ml-2 text-xs text-gray-500">
                          {getOriginalFileNameFromStoragePath(storagePath)}
                        </span>
                      ) : (
                        <span className="ml-2 text-xs text-gray-500">미제출</span>
                      )}
                    </div>

                    <div className="flex gap-2">
                      {!storagePath ? null : (
                        <button
                          onClick={async () => {
                            if (!storagePath) return;
                            try {
                              setExtensionDocumentsDownloading(type);
                              const residentName = residencyInfo.residentInfo?.name || '입주자';
                              await downloadStorageFile(
                                storagePath,
                                `${residencyInfo.roomNumber}호_${residentName}_${getDocumentDisplayName(type)}`
                              );
                            } catch (downloadError) {
                              console.error('연장 신청 첨부파일 다운로드 실패:', downloadError);
                              setExtensionDocumentsError('서류 다운로드 중 오류가 발생했습니다.');
                            } finally {
                              setExtensionDocumentsDownloading(null);
                            }
                          }}
                          disabled={Boolean(extensionDocumentsDownloading)}
                          className={`px-3 py-1.5 rounded text-sm font-['Pretendard'] ${
                            Boolean(extensionDocumentsDownloading)
                              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                              : 'bg-[#155acb] text-white hover:bg-[#155acb]/90'
                          }`}
                        >
                          {isDownloading ? '다운로드 중...' : '다운로드'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* 액션 버튼 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-8">
            <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-6">
              처리 방법 선택
            </h2>
            
            <div className="flex space-x-4">
              <button
                onClick={handleApprove}
                disabled={processing}
                className="px-6 py-3 bg-green-600 text-white rounded-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-['Pretendard'] font-medium"
              >
                {processing ? '처리 중...' : '승인'}
              </button>
              
              <button
                onClick={handleReject}
                disabled={processing}
                className="px-6 py-3 bg-red-600 text-white rounded-sm hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-['Pretendard'] font-medium"
              >
                {processing ? '처리 중...' : '거부'}
              </button>
            </div>
            
            <div className="mt-4 text-sm text-gray-600 font-['Pretendard']">
              <p>• 승인 시 계약 연장이 확정됩니다.</p>
              <p>• 거부 시 거부 사유를 입력해야 하며, 입주자가 확인할 수 있습니다.</p>
            </div>
          </div>
        </div>

        {/* 하단 여백 */}
        <div className="h-16"></div>
      </div>
    </div>
  );
};

export default ExtensionRequestPage; 
